import React, { FC, ReactElement } from 'react';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { FdrImage } from '@hotelplan/fdr.regular.basis.fdr-image';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import { FdrProductFeature } from '@hotelplan/supergraph-api';
import { FdrDisrupters } from 'components/domain/fdr-disrupter';
import { isFdrThGeoObject } from 'components/domain/fdr-geo/fdr-geo.utils';
import {
  getProductBreadcrumbsFromResort,
  isFdrAdventureTravelProduct,
  isFdrHotelProduct,
  isFdrRoundTripProduct,
  isFdrThProduct,
} from 'components/domain/fdr-product/fdr-product.utils';
import { FdrSearchProductInfo } from 'components/domain/fdr-search';
import { CardItemBreadcrumbs } from 'components/domain/recommendations/Recommendations.common';
import FdrBookmarkEditBlock from './fdr-bookmark-edit-block';
import {
  BookmarkCardImg,
  BookmarkItemB2BContent,
  BookmarkItemB2BContentWrapper,
  BookmarkItemB2BIcon,
  BookmarkItemB2BLeadText,
  BookmarkItemHeader,
  BookmarkItemHeaderB2CWrapper,
  BookmarkItemLeadText,
  BookmarkItemLink,
  DisruptersWrap,
  FdrBookmarkBreadcrumbSectionWrapper,
} from './fdr-bookmark-section.styles';
import {
  IBookmarkGroupState,
  TBookmarkItem,
  TFdrBookmarkListProduct,
} from './fdr-bookmark-types';
import {
  getBookmarkedObjectItemFromFdrBookmark,
  getDisrupters,
  mapObjectToBookmarkType,
} from './fdr-bookmark.utils';
import useBookmarkImage from './hooks/useBookmarkImage';

interface IFdrBookmarkItemProps {
  bookmarkObject: TBookmarkItem;
  activeGroup: IBookmarkGroupState;
  features?: FdrProductFeature[];
}

const FdrBookmarkItem: FC<IFdrBookmarkItemProps> = ({
  bookmarkObject,
  activeGroup,
}) => {
  const { channelType } = useAuthentication();
  const isB2B = channelType === AuthChannelType.B2B;

  const bookmarkObjectItem =
    getBookmarkedObjectItemFromFdrBookmark(bookmarkObject);

  const bookmarkImage = useBookmarkImage(bookmarkObject);
  const disrupters = getDisrupters(bookmarkObject);

  if (!bookmarkObjectItem) return null;

  return (
    <FdrRoute link={bookmarkObjectItem.webMeta.descriptionWebMeta.link}>
      <BookmarkItemLink>
        <BookmarkCardImg overlay={!isB2B}>
          {!isB2B && (
            <BookmarkItemHeaderB2CWrapper>
              <BookmarkItemHeader>
                <h3 className="card-title">
                  <span>{bookmarkObjectItem.name}</span>
                  <BsIcon name="chevron-long-right" className="chevron-icon" />
                </h3>
              </BookmarkItemHeader>
            </BookmarkItemHeaderB2CWrapper>
          )}
          {bookmarkImage && <FdrImage image={bookmarkImage} />}
          {disrupters?.length > 0 && (
            <DisruptersWrap>
              <FdrDisrupters disrupters={disrupters} />
            </DisruptersWrap>
          )}
          <FdrBookmarkEditBlock
            objectId={bookmarkObjectItem.id}
            pageType={mapObjectToBookmarkType(bookmarkObject)}
            groupId={activeGroup.id}
            bookmarkId={bookmarkObject.id}
          />
        </BookmarkCardImg>
        {isB2B ? (
          <BookmarkItemB2BContentWrapper>
            <BookmarkItemB2BContent>
              <BookmarkItemHeader>
                <h3 className="card-title">
                  <span>{bookmarkObjectItem.name}</span>
                </h3>
              </BookmarkItemHeader>
              {isFdrThProduct(bookmarkObjectItem) && (
                <>
                  <FdrBookmarkBreadcrumbSection product={bookmarkObjectItem} />
                  <FdrBookmarkItemProductSection product={bookmarkObjectItem} />
                </>
              )}
              {(isFdrThGeoObject(bookmarkObjectItem) ||
                isFdrHotelProduct(bookmarkObjectItem)) && (
                <BookmarkItemB2BLeadText
                  dangerouslySetInnerHTML={{
                    __html: bookmarkObjectItem.leadText,
                  }}
                />
              )}
            </BookmarkItemB2BContent>
            <BookmarkItemB2BIcon>
              <BsIcon
                name="chevron-long-right"
                className="chevron-icon"
                size="lg"
              />
            </BookmarkItemB2BIcon>
          </BookmarkItemB2BContentWrapper>
        ) : (
          <BookmarkItemLeadText
            dangerouslySetInnerHTML={{
              __html: bookmarkObjectItem.leadText,
            }}
          />
        )}
      </BookmarkItemLink>
    </FdrRoute>
  );
};

function FdrBookmarkBreadcrumbSection({
  product,
}: {
  product: TFdrBookmarkListProduct;
}): ReactElement {
  if (isFdrHotelProduct(product)) {
    return (
      <FdrBookmarkBreadcrumbSectionWrapper className="breadcrumb-section">
        <CardItemBreadcrumbs
          className="features-list"
          breadcrumbs={getProductBreadcrumbsFromResort(product.resort)}
        />
      </FdrBookmarkBreadcrumbSectionWrapper>
    );
  }

  return null;
}

function FdrBookmarkItemProductSection({
  product,
}: {
  product: TFdrBookmarkListProduct;
}): ReactElement {
  if (isFdrAdventureTravelProduct(product) || isFdrRoundTripProduct(product)) {
    const productInfoB2B = product.productInfoB2B;

    return (
      <FdrSearchProductInfo
        leadText=""
        route={productInfoB2B?.route}
        duration={productInfoB2B?.duration}
        priceFrom={productInfoB2B?.priceFrom}
        typeFlags={productInfoB2B?.typeFlags}
      />
    );
  }

  return null;
}

export default FdrBookmarkItem;
